import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FieldType, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { SelectOption } from 'app/core/json-schema-controls/models/select-option.model';
import { SelectProps } from 'app/core/json-schema-controls/models/select-props.model';
import { JsonSchemaService } from 'app/core/json-schema-controls/services/json-schema.service';
import { catchError, EMPTY, Observable, Subject, take, takeUntil, tap } from 'rxjs';
@Component({
  selector: 'hmt-select-type',
  standalone: true,
  imports: [FormlyMaterialModule, ReactiveFormsModule, FormlyModule, CommonModule, MatFormFieldModule, MatSelectModule],
  templateUrl: './select-type.component.html',
  styleUrl: './select-type.component.scss',
})
export class SelectTypeComponent extends FieldType<FormlyFieldConfig<SelectProps>> implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  values: SelectOption[] = [];

  /**
   * Returns the form control as a properly typed FormControl
   */
  get selectFormControl(): FormControl {
    return this.formControl as unknown as FormControl;
  }

  constructor(private readonly jsonSchemaService: JsonSchemaService) {
    super();
  }

  ngOnInit(): void {
    this.loadOptionsData();
  }

  /**
   * Loads select options data from props or GraphQL
   */
  private loadOptionsData(): void {
    if (!this.props) {
      return;
    }

    if (this.props.options) {
      this.handlePropOptions();
    } else {
      this.initialDataLoad();
    }
  }

  /**
   * Handles options provided directly through props
   */
  private handlePropOptions(): void {
    if (this.props.options instanceof Observable) {
      this.props.options
        .pipe(
          takeUntil(this.destroy$),
          tap((values: SelectOption[]) => {
            this.values = values;
            this.setInitialValue();
          }),
          catchError(error => {
            console.error('Error loading select options:', error);
            return EMPTY;
          })
        )
        .subscribe();
    } else {
      // Handle case where options is an array directly
      this.values = this.props.options as SelectOption[];
    }
  }

  /**
   * Sets the initial value if it matches one of the loaded options
   */
  private setInitialValue(): void {
    const patchedObject = this.values.find(value => value?.value === this.field.formControl?.value);
    if (patchedObject) {
      this.field.formControl.setValue(patchedObject);
    }
  }

  /**
   * Function to display the label of the selected option
   */
  displayFn(value: SelectOption | null): string | undefined {
    return value ? value.label : undefined;
  }

  /**
   * Loads data from GraphQL using provided query
   */
  private loadDataWithGql(props: FormlyFieldConfig<SelectProps>['props']): Observable<SelectOption[]> {
    return this.jsonSchemaService.graphqlQuery(props as Record<string, unknown>).pipe(
      catchError(error => {
        console.error('GraphQL query error:', error);
        return EMPTY;
      })
    );
  }

  /**
   * Loads initial data from GraphQL if configured
   */
  private initialDataLoad(): void {
    const gqlQuery = this.props.gqlQuery;
    if (gqlQuery?.query && gqlQuery.fields?.label && gqlQuery.fields?.value) {
      this.loadDataWithGql(this.props)
        .pipe(
          take(1),
          tap(result => {
            this.values = result;
            this.setInitialValue();
          })
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
